import { c as _c } from "react/compiler-runtime";
import { OrganizationWalletBadge } from "@/components/ui/OrganizationWalletBadge";
import { findMatchingOrganizationWallet, parseTextWithAddresses } from "@/components/ui/workflow-step-row/components/InlineAddressLink";
import { formatTokenAmount } from "@/components/ui/workflow-step-row";
import { formatCurrencyUsd } from "@/lib/format/currency";
import { containsTemplateVariable, inlineReferenceBadgeClassName, renderValueWithVariables } from "@/components/ui/workflow-step-row/components/VariablePill";
import type { TelegramChat } from "@/lib/telegramBot";
import { cn, formatAddress } from "@/lib/utils";
import React from "react";
import { type ChainInfo, renderWithVariableHighlights } from "../ActionNodeValueFormat";
import { resolveQuickBooksAccountLabel, type QBAccount } from "../form-fields/quickbooksAccounts";
import { resolveSlackConversationLabel, type SlackChannel } from "../form-fields/slackChannels";
import { resolveSlackUserAvatar, resolveSlackUserLabel, type SlackUser } from "../form-fields/slackUsers";
import { resolveTelegramChatLabel } from "../form-fields/telegramChats";
import { useOrgWalletsContext } from "../OrganizationWalletsContext";
export interface NodeSummaryResult {
  summary: React.ReactNode;
  consumedKeys?: string[];
  auxiliary?: React.ReactNode;
}
export interface SummaryContext {
  getNodeName?: (nodeId: string) => string | undefined;
  chainInfoMap?: Map<number, ChainInfo>;
  tokenInfo?: {
    symbol?: string;
    name?: string;
    decimals?: number;
    chainId?: number;
    address?: string;
    metadata?: {
      logoURI?: string | null;
    };
  } | null;
  slackChannelsMap?: Map<string, SlackChannel>;
  slackUsersMap?: Map<string, SlackUser>;
  telegramChatsMap?: Map<string, TelegramChat>;
  quickBooksAccountsMap?: Map<string, QBAccount>;
}
export function SummaryBadge(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(inlineReferenceBadgeClassName, "max-w-full");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <span className={t1}>{children}</span>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function SummaryStack(t0) {
  const $ = _c(10);
  const {
    verb,
    content,
    destination
  } = t0;
  let t1;
  if ($[0] !== verb) {
    t1 = <div className="label-xs">{verb}</div>;
    $[0] = verb;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== content) {
    t2 = <div className="body-xs leading-5">{content}</div>;
    $[2] = content;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== destination) {
    t3 = <div className="label-xs">to {destination}</div>;
    $[4] = destination;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className="space-y-1">{t1}{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export function SummaryPrimary(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="label-xs leading-5">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function SummaryAux(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="label-xs text-ds-text-muted leading-5">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function TextBadge(t0) {
  const $ = _c(9);
  const {
    value,
    getNodeName,
    fallback,
    format
  } = t0;
  if (containsTemplateVariable(value)) {
    let t1;
    if ($[0] !== value) {
      t1 = renderValueWithVariables(value);
      $[0] = value;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <>{t1}</>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== fallback || $[5] !== format || $[6] !== getNodeName || $[7] !== value) {
    t1 = <SummaryBadge><SummaryText value={value} getNodeName={getNodeName} fallback={fallback} format={format} /></SummaryBadge>;
    $[4] = fallback;
    $[5] = format;
    $[6] = getNodeName;
    $[7] = value;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}
export function SummaryText(t0) {
  const $ = _c(19);
  const {
    value,
    getNodeName,
    fallback,
    format
  } = t0;
  const {
    wallets
  } = useOrgWalletsContext();
  if (value === null || value === undefined || value === "") {
    let t1;
    if ($[0] !== fallback) {
      t1 = <>{fallback}</>;
      $[0] = fallback;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (containsTemplateVariable(value)) {
    let t1;
    if ($[2] !== value) {
      t1 = renderValueWithVariables(value);
      $[2] = value;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== t1) {
      t2 = <>{t1}</>;
      $[4] = t1;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  if (format === "currency" && (typeof value === "number" || typeof value === "string")) {
    let t1;
    if ($[6] !== value) {
      t1 = formatCurrencyUsd(value);
      $[6] = value;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    let t2;
    if ($[8] !== t1) {
      t2 = <>{t1}</>;
      $[8] = t1;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  if (typeof value === "string") {
    let t1;
    let t2;
    if ($[10] !== getNodeName || $[11] !== value || $[12] !== wallets) {
      t2 = Symbol.for("react.early_return_sentinel");
      bb0: {
        const compact = value.replace(/\s+/g, " ").trim();
        const matchingWallets = wallets.filter(wallet => {
          const normalizedValue = compact.toLowerCase();
          return normalizedValue.includes(wallet.id.toLowerCase()) || normalizedValue.includes(wallet.address.toLowerCase()) || normalizedValue.includes(formatAddress(wallet.address).toLowerCase());
        });
        if (matchingWallets.length > 0) {
          t2 = <>{parseTextWithAddresses(compact, undefined, wallets, "summary", false)}</>;
          break bb0;
        }
        t1 = renderWithVariableHighlights(compact, getNodeName);
      }
      $[10] = getNodeName;
      $[11] = value;
      $[12] = wallets;
      $[13] = t1;
      $[14] = t2;
    } else {
      t1 = $[13];
      t2 = $[14];
    }
    if (t2 !== Symbol.for("react.early_return_sentinel")) {
      return t2;
    }
    let t3;
    if ($[15] !== t1) {
      t3 = <>{t1}</>;
      $[15] = t1;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    return t3;
  }
  const t1 = String(value);
  let t2;
  if ($[17] !== t1) {
    t2 = <>{t1}</>;
    $[17] = t1;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  return t2;
}
export function SummaryAddress(t0) {
  const $ = _c(15);
  const {
    value,
    fallback
  } = t0;
  const {
    wallets
  } = useOrgWalletsContext();
  if (typeof value !== "string" || !value.trim()) {
    let t1;
    if ($[0] !== fallback) {
      t1 = <>{fallback}</>;
      $[0] = fallback;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (containsTemplateVariable(value)) {
    let t1;
    if ($[2] !== value) {
      t1 = renderValueWithVariables(value);
      $[2] = value;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== t1) {
      t2 = <>{t1}</>;
      $[4] = t1;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t1;
  if ($[6] !== value || $[7] !== wallets) {
    t1 = findMatchingOrganizationWallet(value, wallets);
    $[6] = value;
    $[7] = wallets;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const matchingWallet = t1;
  if (matchingWallet) {
    let t2;
    if ($[9] !== matchingWallet) {
      t2 = <OrganizationWalletBadge wallet={matchingWallet} variant="summary" showAddress={false} />;
      $[9] = matchingWallet;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    return t2;
  }
  let t2;
  if ($[11] !== value) {
    t2 = value.startsWith("0x") ? formatAddress(value) : value;
    $[11] = value;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== t2) {
    t3 = <>{t2}</>;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
}
export function SummaryChain(t0) {
  const $ = _c(7);
  const {
    chainId,
    chainInfoMap,
    fallback: t1
  } = t0;
  const fallback = t1 === undefined ? "chain" : t1;
  if (typeof chainId !== "number" || !Number.isFinite(chainId)) {
    let t2;
    if ($[0] !== fallback) {
      t2 = <>{fallback}</>;
      $[0] = fallback;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t2;
  if ($[2] !== chainId || $[3] !== chainInfoMap) {
    t2 = chainInfoMap?.get(chainId)?.name || `Chain ${chainId}`;
    $[2] = chainId;
    $[3] = chainInfoMap;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <>{t2}</>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
export function SummaryAmount(t0) {
  const $ = _c(13);
  const {
    value,
    decimals,
    fallback
  } = t0;
  if (value === null || value === undefined || value === "") {
    let t1;
    if ($[0] !== fallback) {
      t1 = <>{fallback}</>;
      $[0] = fallback;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (containsTemplateVariable(value)) {
    let t1;
    if ($[2] !== value) {
      t1 = renderValueWithVariables(value);
      $[2] = value;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== t1) {
      t2 = <>{t1}</>;
      $[4] = t1;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  if ((typeof value === "string" || typeof value === "number") && typeof decimals === "number") {
    let t1;
    if ($[6] !== decimals || $[7] !== value) {
      t1 = formatTokenAmount(value, decimals);
      $[6] = decimals;
      $[7] = value;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    let t2;
    if ($[9] !== t1) {
      t2 = <>{t1}</>;
      $[9] = t1;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    return t2;
  }
  const t1 = String(value);
  let t2;
  if ($[11] !== t1) {
    t2 = <>{t1}</>;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
}
export function SummarySlackChannel(t0) {
  const $ = _c(11);
  const {
    value,
    slackChannelsMap
  } = t0;
  if (value === null || value === undefined || value === "") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <>Slack channel</>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (containsTemplateVariable(value)) {
    let t1;
    if ($[1] !== value) {
      t1 = renderValueWithVariables(value);
      $[1] = value;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== t1) {
      t2 = <>{t1}</>;
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (typeof value === "string") {
    let t1;
    if ($[5] !== slackChannelsMap || $[6] !== value) {
      t1 = resolveSlackConversationLabel(value, slackChannelsMap) || "Slack channel";
      $[5] = slackChannelsMap;
      $[6] = value;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    let t2;
    if ($[8] !== t1) {
      t2 = <>{t1}</>;
      $[8] = t1;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  let t1;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <>Slack channel</>;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}
export function SummaryTelegramChat(t0) {
  const $ = _c(10);
  const {
    value,
    telegramChatsMap
  } = t0;
  if (value === null || value === undefined || value === "") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <>Telegram chat</>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (containsTemplateVariable(value)) {
    let t1;
    if ($[1] !== value) {
      t1 = renderValueWithVariables(value);
      $[1] = value;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== t1) {
      t2 = <>{t1}</>;
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t1;
  if ($[5] !== telegramChatsMap || $[6] !== value) {
    t1 = resolveTelegramChatLabel(String(value), telegramChatsMap) || "Telegram chat";
    $[5] = telegramChatsMap;
    $[6] = value;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== t1) {
    t2 = <>{t1}</>;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
export function SummaryQuickBooksAccount(t0) {
  const $ = _c(12);
  const {
    value,
    quickBooksAccountsMap,
    fallback: t1
  } = t0;
  const fallback = t1 === undefined ? "account" : t1;
  if (value === null || value === undefined || value === "") {
    let t2;
    if ($[0] !== fallback) {
      t2 = <>{fallback}</>;
      $[0] = fallback;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  if (containsTemplateVariable(value)) {
    let t2;
    if ($[2] !== value) {
      t2 = renderValueWithVariables(value);
      $[2] = value;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t2) {
      t3 = <>{t2}</>;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t2;
  if ($[6] !== fallback || $[7] !== quickBooksAccountsMap || $[8] !== value) {
    t2 = resolveQuickBooksAccountLabel(String(value), quickBooksAccountsMap) || fallback;
    $[6] = fallback;
    $[7] = quickBooksAccountsMap;
    $[8] = value;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t2) {
    t3 = <>{t2}</>;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
export function SlackChannelBadge(t0) {
  const $ = _c(7);
  const {
    value,
    slackChannelsMap
  } = t0;
  if (containsTemplateVariable(value)) {
    let t1;
    if ($[0] !== value) {
      t1 = renderValueWithVariables(value);
      $[0] = value;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <>{t1}</>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== slackChannelsMap || $[5] !== value) {
    t1 = <SummaryBadge><SummarySlackChannel value={value} slackChannelsMap={slackChannelsMap} /></SummaryBadge>;
    $[4] = slackChannelsMap;
    $[5] = value;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}

/** Renders a Slack user id as a badge showing their avatar + display name. */
export function SlackUserBadge(t0) {
  const $ = _c(18);
  const {
    value,
    slackUsersMap,
    fallback: t1
  } = t0;
  const fallback = t1 === undefined ? "a user" : t1;
  if (containsTemplateVariable(value)) {
    let t2;
    if ($[0] !== value) {
      t2 = renderValueWithVariables(value);
      $[0] = value;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== t2) {
      t3 = <>{t2}</>;
      $[2] = t2;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    return t3;
  }
  if (typeof value !== "string" || !value.trim()) {
    let t2;
    if ($[4] !== fallback) {
      t2 = <SummaryBadge>{fallback}</SummaryBadge>;
      $[4] = fallback;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t2;
  if ($[6] !== fallback || $[7] !== slackUsersMap || $[8] !== value) {
    t2 = resolveSlackUserLabel(value, slackUsersMap) || fallback;
    $[6] = fallback;
    $[7] = slackUsersMap;
    $[8] = value;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const label = t2;
  let t3;
  if ($[10] !== slackUsersMap || $[11] !== value) {
    t3 = resolveSlackUserAvatar(value, slackUsersMap);
    $[10] = slackUsersMap;
    $[11] = value;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const avatar = t3;
  let t4;
  if ($[13] !== avatar) {
    t4 = avatar ? <img src={avatar} alt="" loading="lazy" className="mr-1 inline-block h-4 w-4 flex-shrink-0 rounded-full object-cover align-middle" /> : null;
    $[13] = avatar;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== label || $[16] !== t4) {
    t5 = <SummaryBadge>{t4}{label}</SummaryBadge>;
    $[15] = label;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}
export function TelegramChatBadge(t0) {
  const $ = _c(7);
  const {
    value,
    telegramChatsMap
  } = t0;
  if (containsTemplateVariable(value)) {
    let t1;
    if ($[0] !== value) {
      t1 = renderValueWithVariables(value);
      $[0] = value;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <>{t1}</>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== telegramChatsMap || $[5] !== value) {
    t1 = <SummaryBadge><SummaryTelegramChat value={value} telegramChatsMap={telegramChatsMap} /></SummaryBadge>;
    $[4] = telegramChatsMap;
    $[5] = value;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}
export function QuickBooksAccountBadge(t0) {
  const $ = _c(8);
  const {
    value,
    quickBooksAccountsMap,
    fallback
  } = t0;
  if (containsTemplateVariable(value)) {
    let t1;
    if ($[0] !== value) {
      t1 = renderValueWithVariables(value);
      $[0] = value;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <>{t1}</>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== fallback || $[5] !== quickBooksAccountsMap || $[6] !== value) {
    t1 = <SummaryBadge><SummaryQuickBooksAccount value={value} quickBooksAccountsMap={quickBooksAccountsMap} fallback={fallback} /></SummaryBadge>;
    $[4] = fallback;
    $[5] = quickBooksAccountsMap;
    $[6] = value;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
export function createStackedSummary({
  verb,
  content,
  destination,
  consumedKeys,
  auxiliary
}: {
  verb: string;
  content: React.ReactNode;
  destination: React.ReactNode;
  consumedKeys: string[];
  auxiliary?: React.ReactNode;
}): NodeSummaryResult {
  return {
    summary: <SummaryStack verb={verb} content={content} destination={destination} />,
    consumedKeys,
    auxiliary
  };
}
export function createPrimarySummary({
  summary,
  consumedKeys,
  auxiliary
}: {
  summary: React.ReactNode;
  consumedKeys: string[];
  auxiliary?: React.ReactNode;
}): NodeSummaryResult {
  return {
    summary: <SummaryPrimary>{summary}</SummaryPrimary>,
    consumedKeys,
    auxiliary
  };
}
export function createTransferSummary({
  verb,
  amount,
  decimals,
  asset,
  chainId,
  recipient,
  chainInfoMap
}: {
  verb: string;
  amount: unknown;
  decimals?: number;
  asset: React.ReactNode;
  chainId: unknown;
  recipient: unknown;
  chainInfoMap?: Map<number, ChainInfo>;
}): NodeSummaryResult {
  return createStackedSummary({
    verb,
    content: <>
        <SummaryAmount value={amount} decimals={decimals} fallback="amount" /> {asset} on{" "}
        <SummaryChain chainId={chainId} chainInfoMap={chainInfoMap} />
      </>,
    destination: <SummaryAddress value={recipient} fallback="recipient" />,
    consumedKeys: ["amount", "chainId", "recipientAddress", "tokenAddress"]
  });
}